// Proton ModalTwo (packages/components/components/modalTwo): backdrop, a
// centred dialog (sizes small / medium / large / xlarge, plus the 288px
// Prompt used for confirmations), header with title + close, scrollable
// content, footer. Styles in src/proton/components.css (.modal-two*).
import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { X } from "lucide-react";
import { Button } from "./Button";
import { Icon } from "./Icon";

export type ModalSize = "prompt" | "small" | "medium" | "large" | "xlarge";

export function Modal({
  onClose, size = "medium", className, dialogClassName, role = "dialog", labelledBy, describedBy,
  closeOnEscape = true, closeOnBackdrop = true, children,
}: {
  onClose?: () => void;
  size?: ModalSize;
  className?: string;
  dialogClassName?: string;
  role?: "dialog" | "alertdialog";
  labelledBy?: string;
  describedBy?: string;
  /** Callers with their own key handling (the confirm dialog) turn this off. */
  closeOnEscape?: boolean;
  closeOnBackdrop?: boolean;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!closeOnEscape || !onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [closeOnEscape, onClose]);

  return createPortal(
    <>
      <div className="modal-two-backdrop" aria-hidden="true" />
      <div
        className={clsx("modal-two", className)}
        role="presentation"
        onMouseDown={(e) => {
          // Backdrop click; a drag that started inside the dialog doesn't count.
          if (closeOnBackdrop && e.target === e.currentTarget) onClose?.();
        }}
      >
        <div
          className={clsx("modal-two-dialog", size !== "medium" && `modal-two-dialog--${size}`, dialogClassName)}
          role={role}
          aria-modal="true"
          aria-labelledby={labelledBy}
          aria-describedby={describedBy}
        >
          <div className="modal-two-dialog-container">{children}</div>
        </div>
      </div>
    </>,
    document.body,
  );
}

export function ModalHeader({
  title, subline, id, onClose,
}: { title?: ReactNode; subline?: ReactNode; id?: string; onClose?: () => void }) {
  return (
    <div className="modal-two-header">
      <div className={clsx("flex flex-nowrap shrink-0 items-start", title ? "justify-between" : "justify-end")}>
        {title && (
          <div className="modal-two-header-title">
            <h1 id={id}>{title}</h1>
            {subline && <div className="color-weak">{subline}</div>}
          </div>
        )}
        {onClose && (
          <div className="modal-two-header-actions">
            <Button shape="ghost" icon title="Close" aria-label="Close" onClick={onClose}>
              <Icon as={X} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function ModalContent({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={clsx("modal-two-content", className)}>{children}</div>;
}

export function ModalFooter({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={clsx("modal-two-footer", className)}>{children}</div>;
}
